<template>
  <div class="flex flex-col">
    <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
      <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
        <div class="overflow-hidden">
          <template v-if="rows && rows.length">
            <div class="w-full">
              <div v-if="withTitles">
                <div class="text-left">
                  <div
                    v-if="selectable"
                    class="pl-6 py-2 px-3 sticky top-0 bg-white w-5"
                  >
                    <Checkbox
                      class="inline-block"
                      :checked="resourcesAreSelected"
                      @change="handleSelectAllButtonClick"
                      color="primary"
                      slot="start-icon"
                    />
                  </div>
                  <div
                    v-for="(column, index) in columnsOptions"
                    :key="index"
                    class="px-6 py-4 bg-white text-sm text-secondary-dark font-normal"
                  >
                    {{ column.title }}
                  </div>
                  <div
                    v-if="actions.length"
                    class="px-6 py-4 bg-white text-sm text-secondary-dark font-normal"
                  >
                    Actions
                  </div>
                </div>
              </div>
              <div class="grid gap-4 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
                <div
                  v-for="row in rows"
                  :key="row._id"
                  class="flex bg-white truncate border-dull border flex-col rounded"
                  @click="handleRowClick(row)"
                >

                <div class="header flex items-center p-4">
                  <Typography class="truncate" variant="headingFive">{{
                    row.name
                  }}</Typography>
                  <div class="w-12 ml-auto" v-if="actions.length">
                      <Menu
                        placement="bottom-end"
                        :open="row.open"
                        @close="row.open = false"
                      >
                        <div
                          slot="reference"
                          class="p-1 flex items-center justify-center cursor-pointer"
                          @click.stop="handleRowMenuClick(row)"
                        >
                          <Icon name="ellipsis-h" />
                        </div>

                        <ul class="p-2 list-none m-0 text-secondary">
                          <MenuItem
                            v-if="showEditAction"
                            @click.stop="
                              handleRowMenuItemClick('row-action-edit', row)
                            "
                          >
                            <Icon name="cog" class="mr-2" /> Edit
                          </MenuItem>
                          <MenuItem
                            v-if="showEditAction"
                            @click.stop="
                              handleRowMenuItemClick('row-action-analytics', row)
                            "
                          >
                            <Icon name="chart-bar" class="mr-2" /> Analytics
                          </MenuItem>
                          <MenuItem
                            v-if="showCloneAction"
                            @click.stop="
                              handleRowMenuItemClick('row-action-clone', row)
                            "
                          >
                            <Icon name="copy" class="mr-2" /> Duplicate
                          </MenuItem>
                          <MenuItem
                            v-if="showTransferAction"
                            @click.stop="
                              handleRowMenuItemClick('row-action-transfer', row)
                            "
                          >
                            <Icon name="random" class="mr-2" /> Transfer
                          </MenuItem>
                          <MenuItem
                            v-if="showShareAction"
                            @click.stop="
                              handleRowMenuItemClick('row-action-share', row)
                            "
                          >
                            <Icon name="share" class="mr-2" /> Share
                          </MenuItem>
                          <MenuItem
                            v-if="showUnpublishAction"
                            @click.stop="
                              handleRowMenuItemClick('row-action-unpublish', row)
                            "
                          >
                            <Icon name="power-off" class="mr-2" /> Unpublish
                          </MenuItem>
                          <MenuItem
                            v-if="showExportAction"
                            @click.stop="
                              handleRowMenuItemClick('row-action-export', row)
                            "
                          >
                            <Icon name="download" class="mr-2" /> Export
                          </MenuItem>
                          <MenuItem
                            v-if="showDeleteAction"
                            @click.stop="
                              handleRowMenuItemClick('row-action-delete', row)
                            "
                          >
                            <Icon name="trash" class="mr-2" /> Delete
                          </MenuItem>
                        </ul>
                      </Menu>
                    </div>
                  </div>

                  <div v-if="selectable" class="pl-6 py-4 w-5">
                    <Checkbox
                      color="primary"
                      class="mr-4 inline-block"
                      v-model="row.selected"
                      @change="handleSelectClick"
                    />
                  </div>

                  <div
                    v-for="(column, index) in columnsOptions"
                    :key="index"
                    class="whitespace-no-wrap"
                    :class="column.classes"
                  >
                    <div class="text-dull-dark">
                      <template v-if="column.scopedSlot">
                        <vnodes
                          :vnodes="column.scopedSlot({row, items: rows})"
                        />
                      </template>
                      <template v-else>{{ getProp(row, column) }}</template>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="w-full p-8 text-center bg-white border border-dull rounded">
              <div class="text-center">
                <Icon class="text-4xl text-dull mb-2" name="browser" />
                  <h3 class="mt-2 text-md font-medium text-secondary-dark">No sites</h3>
                  <p class="mt-1 text-sm text-dull-dark">
                  Get started by creating a new site.
                  </p>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {get} from 'lodash';
import Icon from '../Icon';
import Divider from '../Divider';
import Checkbox from '../Checkbox';
import Button from '../Button';

export default {
  name: 'GridTable',
  components: {
    Icon,
    Divider,
    Checkbox,
    Button,
    Vnodes: {
      functional: true,
      render: (h, ctx) => ctx.props.vnodes,
    },
  },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    withTitles: {
      type: Boolean,
      default: false,
    },
    actions: {
      type: Array,
      default: () => [],
    },
    border: {
      type: Boolean,
      default: true,
    },
    rounded: {
      type: Boolean,
      default: true,
    },
  },
  methods: {
    getWidth(column) {
      const width = column.width;
      return {[width]: width !== undefined};
    },
    getProp(row, column) {
      return get(row, column.prop);
    },
    handleSelectClick() {
      console.log('selection_ids', this.selection_ids);
      this.$emit('selection', this.selection_ids);
    },
    handleSelectAllButtonClick() {
      this.selectAll(!this.resourcesAreSelected);
      this.$emit('selection', this.selection_ids);
    },
    selectAll(value) {
      this.rows.forEach((row) => this.$set(row, 'selected', value));
    },
    handleRowClick(row) {
      this.$emit('row-click', row);
    },
    handleRowMenuClick(row) {
      this.rows.map((row) => this.$set(row, 'open', false));
      this.$set(row, 'open', !row.open);
    },
    handleRowMenuItemClick(action, row) {
      this.$set(row, 'open', false);
      this.$emit(action, row);
    },
  },
  computed: {
    rows() {
      return this.data ? this.data : [];
    },
    selection() {
      return this.rows ? this.rows.filter((row) => row.selected === true) : [];
    },
    selection_ids() {
      return this.selection.map((row) => row._id);
    },
    tableEmpty() {
      const tableEmpty = this.$slots.default.filter((item) => {
        return (
          item.componentOptions && item.componentOptions.tag === 'TableEmpty'
        );
      })[0];
      if (tableEmpty) {
        return Object.assign({}, tableEmpty.componentOptions.propsData, {
          scopedSlot: get(tableEmpty, 'data.scopedSlots.default'),
        });
      }
      return undefined;
    },
    columnsOptions() {
      return this.$slots.default
        .filter((item) => {
          return (
            item.componentOptions && item.componentOptions.tag === 'TableColumn'
          );
        })
        .map((column) => {
          return Object.assign({}, column.componentOptions.propsData, {
            scopedSlot: get(column, 'data.scopedSlots.default'),
          });
        });
    },
    resourcesAreSelected() {
      return this.selection.length > 0;
    },
    showActions() {
      return this.actions.length;
    },
    showEditAction() {
      return this.actions.includes('edit');
    },
    showTransferAction() {
      return this.actions.includes('transfer');
    },
    showShareAction() {
      return this.actions.includes('share');
    },
    showCloneAction() {
      return this.actions.includes('clone');
    },
    showUnpublishAction() {
      return this.actions.includes('unpublish');
    },
    showExportAction() {
      return this.actions.includes('export');
    },
    showDeleteAction() {
      return this.actions.includes('delete');
    },
  },
};
</script>

<style scoped>
tbody {
  display: grid;
}
/* .table-row {
  @apply cursor-pointer;
} */

/* .table-row:hover {
  @apply bg-dull-light;
} */
</style>
